/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * Payout math. Pure functions (no I/O) so they're trivially testable and reused
 * by both the scoring UI and the PDF export. The earned payout is computed at
 * scoring time and stored on incentive_scores.payout_earned (an immutable
 * record), so later edits to a goal's structure never silently restate history.
 */

// Coerce a possibly-string/blank numeric field to a finite number (default 0).
export function num(v, fallback = 0) {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

// Percent of target reached. With no positive target there's no meaningful
// attainment, so report 0% rather than a misleading "100% of target".
export function attainmentPct(actual, target) {
  const a = num(actual), t = num(target);
  if (t <= 0) return 0;
  return (a / t) * 100;
}

// Tiers: [{ min_value, amount }]. Highest tier whose min_value <= actual wins.
function tieredPayout(actual, tiers) {
  const a = num(actual);
  const sorted = (Array.isArray(tiers) ? tiers : [])
    .map((t) => ({ min: num(t.min_value), amount: num(t.amount) }))
    .filter((t) => Number.isFinite(t.min))
    .sort((x, y) => x.min - y.min);
  let earned = 0;
  for (const t of sorted) {
    if (a >= t.min) earned = t.amount;
    else break;
  }
  return earned;
}

/**
 * Earned payout for one goal given the achieved actual.
 *   flat     → full amount once the target is met, else 0
 *   per_unit → amount × actual units achieved
 *   tiered   → amount of the highest reached tier
 * // DECISION: per_unit pays on every achieved unit (no cap at target); a cap
 * // can be modeled with a tiered structure if a company needs one.
 */
export function computePayout(goal, actual) {
  const a = num(actual);
  const amount = num(goal?.payout_amount);
  switch (goal?.payout_type) {
    case "custom":
      // Can't be computed from a formula — the payout is entered manually at
      // scoring time. Signal "no computed value" with null.
      return null;
    case "per_unit":
      return amount * a;
    case "tiered":
      return tieredPayout(a, goal?.payout_tiers);
    case "flat":
    default: {
      // Flat pays only when a real (positive) target is met — a blank/0 target
      // must NOT auto-pay (0 >= 0 would otherwise always be "met").
      const t = num(goal?.target_value);
      return t > 0 && a >= t ? amount : 0;
    }
  }
}

// A custom payout can't be auto-computed; scoring must collect the amount.
export function isComputablePayout(goal) {
  return goal?.payout_type !== "custom";
}

// Human-readable summary of a goal's payout structure (for cards + PDF).
export function describePayout(goal) {
  const money = (n) => `$${num(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  switch (goal?.payout_type) {
    case "custom":
      return goal?.payout_custom?.trim() || "Custom payout";
    case "per_unit":
      return `${money(goal?.payout_amount)} per ${goal?.unit_label || "unit"}`;
    case "tiered": {
      const tiers = Array.isArray(goal?.payout_tiers) ? goal.payout_tiers : [];
      if (!tiers.length) return "Tiered (no tiers defined)";
      return tiers
        .map((t) => `${num(t.min_value).toLocaleString()}+ → ${money(t.amount)}`)
        .join(" · ");
    }
    case "flat":
    default:
      return `${money(goal?.payout_amount)} when target met`;
  }
}
